import type { RefObject } from 'react';
import { Box, Drawer, useTheme } from '@mui/material';
import { getWorkspaceDrawerPaperSx } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import {
  PropertySelectionPanel,
  type AvailableParameter,
  type PropertyItem,
} from '../../../shared/ui/propertySelection/PropertySelectionPanel';
import { EXPORT_COLUMN_PANEL_ID } from '../exportPcfConstants';

interface ExportColumnPanelsProps {
  open: boolean;
  useDrawer: boolean;
  persistentDrawer: boolean;
  properties: PropertyItem[];
  extraProperties: PropertyItem[];
  availableParameters: AvailableParameter[];
  inlinePanelRef: RefObject<HTMLDivElement | null>;
  drawerPanelRef: RefObject<HTMLDivElement | null>;
  inlineSearchRef: RefObject<HTMLInputElement | null>;
  drawerSearchRef: RefObject<HTMLInputElement | null>;
  onCheckedChange: (id: string, checked: boolean) => void;
  onAdd: (property: PropertyItem) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
}

export default function ExportColumnPanels(props: ExportColumnPanelsProps) {
  const theme = useTheme();
  const panel = (searchRef: RefObject<HTMLInputElement | null>) => (
    <PropertySelectionPanel
      properties={props.properties}
      extraProperties={props.extraProperties}
      searchInputRef={searchRef}
      sharedParameters={[]}
      availableParameters={props.availableParameters}
      workflowMode="standard"
      workflowParameters={[]}
      onPropertyCheckedChange={props.onCheckedChange}
      onParameterChange={() => undefined}
      onAddExtraProperty={props.onAdd}
      onRemoveExtraProperty={props.onRemove}
      expanded
      onExpandChange={props.onClose}
    />
  );

  return (
    <>
      {props.open && !props.useDrawer ? (
        <Box
          id={EXPORT_COLUMN_PANEL_ID}
          ref={props.inlinePanelRef}
          data-testid="export-pcf-tour-target-column-panel"
          sx={{
            width: 'clamp(320px, 32vw, 420px)',
            maxWidth: '38vw',
            height: '100%',
            minHeight: 0,
            flexShrink: 0,
            animation: 'slideIn 0.3s ease-out',
            '@keyframes slideIn': {
              from: { opacity: 0, transform: 'translateX(20px)' },
              to: { opacity: 1, transform: 'translateX(0)' },
            },
          }}
        >
          {panel(props.inlineSearchRef)}
        </Box>
      ) : null}
      <Drawer
        anchor="right"
        open={props.useDrawer && props.open}
        onClose={props.onClose}
        variant={props.persistentDrawer ? 'persistent' : 'temporary'}
        hideBackdrop={props.persistentDrawer}
        ModalProps={{
          keepMounted: true,
          disableAutoFocus: props.persistentDrawer,
          disableEnforceFocus: props.persistentDrawer,
          disableRestoreFocus: props.persistentDrawer,
          disablePortal: props.persistentDrawer,
        }}
        PaperProps={{
          sx: [getWorkspaceDrawerPaperSx(theme, 'right'), {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100vw',
          }],
        }}
      >
        <Box
          id={props.useDrawer ? EXPORT_COLUMN_PANEL_ID : undefined}
          ref={props.drawerPanelRef}
          data-testid={props.useDrawer ? 'export-pcf-tour-target-column-panel' : undefined}
          sx={{ width: '100%', height: '100%', minHeight: 0, overflow: 'hidden' }}
        >
          {panel(props.drawerSearchRef)}
        </Box>
      </Drawer>
    </>
  );
}
